import { AnimatePresence, motion } from 'framer-motion';
import { useState } from 'react';
import GameCanvas from './components/GameCanvas';
import HUD from './components/HUD';
import { MinimalLanding } from './pages/MinimalLanding';
import { disposeLiveClient } from './net/liveSession';

type View = 'home' | 'game';

/**
 * Root view router.
 *
 * Every visitor lands on the Home page first. Only when they click "Play"
 * we mount the GameCanvas + HUD (which is when the heavy engine actually
 * starts). This keeps the home page lightweight + makes room for ad slots.
 *
 * Returning to home fully unmounts the engine (dispose() runs in
 * GameCanvas's useEffect cleanup), so memory + intervals are released.
 *
 * Transition: a thin crossfade (180ms) between landing and game so the
 * cut doesn't feel jarring. The engine itself still mounts instantly
 * inside the animated container.
 */
export default function App() {
  const [view, setView] = useState<View>('home');

  return (
    <main className="relative w-full h-full bg-bg-deep overflow-hidden">
      <AnimatePresence mode="wait" initial={false}>
        {view === 'home' && (
          <motion.div
            key="home"
            className="absolute inset-0"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
          >
            <MinimalLanding onPlay={() => setView('game')} />
          </motion.div>
        )}
        {view === 'game' && (
          <motion.div
            key="game"
            className="absolute inset-0"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.22 }}
          >
            <GameCanvas />
            <HUD onHome={() => {
              // Cleanly tear down the multiplayer session before
              // routing back to the landing. Without this, the WS
              // would stay open with the player still counted in the
              // arena's roster long after they navigated away.
              disposeLiveClient();
              setView('home');
            }} />
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
}
